import { useCallback, useEffect, useRef, useState } from 'react'
import { Html5Qrcode } from 'html5-qrcode'
import { Button } from './ui/Button'

interface Props {
  onScan: (code: string) => void
  label?: string
}

export function BarcodeScanner({ onScan, label = 'Scan Barcode' }: Props) {
  const [open, setOpen] = useState(false)
  const scannerRef = useRef<Html5Qrcode | null>(null)
  const containerId = 'barcode-scanner-container'

  const stop = useCallback(async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop()
      } catch {
        // already stopped
      }
      scannerRef.current.clear()
      scannerRef.current = null
    }
  }, [])

  const start = useCallback(async () => {
    const scanner = new Html5Qrcode(containerId)
    scannerRef.current = scanner
    try {
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 280, height: 120 } },
        (decodedText) => {
          void stop()
          setOpen(false)
          onScan(decodedText)
        },
        undefined,
      )
    } catch {
      await stop()
      setOpen(false)
    }
  }, [onScan, stop])

  useEffect(() => {
    if (open) {
      // small delay so the container div is mounted
      const t = setTimeout(() => { void start() }, 100)
      return () => clearTimeout(t)
    } else {
      void stop()
    }
  }, [open, start, stop])

  // cleanup on unmount
  useEffect(() => () => { void stop() }, [stop])

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} type="button">
        {label}
      </Button>

      {open && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setOpen(false)}
        >
          <div
            style={{ background: 'var(--surface)', borderRadius: 12, padding: 24, maxWidth: 360, width: '100%' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ marginBottom: 12, fontWeight: 600, color: 'var(--text-heading)' }}>Scan barcode</div>
            <div id={containerId} />
            <div style={{ marginTop: 12, textAlign: 'center' }}>
              <Button variant="outline" size="sm" onClick={() => setOpen(false)} type="button">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
